import React from 'react';

export type JanusMarkState = 'processing' | 'completed' | 'failed';

interface JanusEnrichingMarkProps {
  size?: number;
  className?: string;
  /** light = backplate `#FAFAFE` (uso inline em fundo claro);
   *  dark  = transparente (uso embutido em badges coloridos). */
  variant?: 'light' | 'dark';
  /** When false, all internal animations are paused — useful to render a
   *  still version inside a button label, a paused enrichment, or PDFs. */
  animated?: boolean;
  /** Visual state:
   *   - processing: eye dilates, arcs scan, diamond ripples (default)
   *   - completed:  eye replaced by check, arcs paused, diamond solid violet
   *   - failed:     eye replaced by X mark, amber stop tones */
  state?: JanusMarkState;
}

/**
 * JanusEnrichingMark — variante animada do logo JanusOS dedicada a
 * sinalizar enriquecimento em curso. Combina:
 *   - dois arcos laterais longos (aura/scanning) oscilando ±4°
 *   - silhuetas de duas cabeças em perfil mirroradas (referência mítica
 *     ao deus Janus, símbolo de ver dois mundos ao mesmo tempo)
 *   - diamante em três camadas concêntricas com ripple em cascata e
 *     borda violeta reforçada
 *   - olho central com íris/pupila pulsando como "descoberta"
 *
 * O texto "JanusOS" do logo principal NÃO é renderizado aqui —
 * este componente é só o símbolo, self-contained dentro do viewBox.
 *
 * Estados:
 *   - processing → animações coordenadas, paleta violeta
 *   - completed  → olho vira check verde, arcs pausam (sucesso)
 *   - failed     → olho vira X âmbar, arcs pausam (erro)
 */
const JanusEnrichingMark: React.FC<JanusEnrichingMarkProps> = ({
  size = 28,
  className,
  variant = 'light',
  animated = true,
  state = 'processing',
}) => {
  const id = `jem-${size}-${state}`;
  const bgFill = variant === 'light' ? '#FAFAFE' : 'transparent';
  const isProcessing = state === 'processing';
  const isCompleted = state === 'completed';
  const isFailed = state === 'failed';

  // Only the processing state animates — terminal states are static so
  // the user can read them at a glance.
  const animate = animated && isProcessing;
  const arcLeftClass = animate ? 'janus-arc-orbit-left' : undefined;
  const arcRightClass = animate ? 'janus-arc-orbit-right' : undefined;
  const eyeClass = animate ? 'janus-eye-discover' : undefined;
  const outerClass = animate ? 'janus-diamond-outer' : undefined;
  const midClass = animate ? 'janus-diamond-mid' : undefined;
  const innerClass = animate ? 'janus-diamond-inner' : undefined;

  // Terminal-state color overrides.
  const innerFill = isCompleted
    ? 'rgba(209,250,229,0.95)'
    : isFailed
      ? 'rgba(254,243,199,0.95)'
      : 'rgba(237,233,254,0.9)';
  const innerStroke = isCompleted
    ? 'rgba(16,185,129,0.55)'
    : isFailed
      ? 'rgba(217,119,6,0.55)'
      : 'rgba(99,102,241,0.45)';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={
        isCompleted
          ? 'Enrichment completed'
          : isFailed
            ? 'Enrichment failed'
            : 'AI Enriching'
      }
    >
      <defs>
        <linearGradient
          id={`${id}-arc`}
          x1="0"
          y1="6"
          x2="0"
          y2="58"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#C4B5FD" />
          <stop offset="50%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#4F46E5" />
        </linearGradient>

        <linearGradient
          id={`${id}-diamond-outer`}
          x1="0"
          y1="0"
          x2="64"
          y2="64"
          gradientUnits="userSpaceOnUse"
        >
          <stop
            offset="0%"
            stopColor={isCompleted ? '#34D399' : isFailed ? '#FBBF24' : '#A78BFA'}
            stopOpacity="0.6"
          />
          <stop
            offset="100%"
            stopColor={isCompleted ? '#059669' : isFailed ? '#D97706' : '#4F46E5'}
            stopOpacity="0.92"
          />
        </linearGradient>

        <linearGradient
          id={`${id}-diamond-mid`}
          x1="0"
          y1="0"
          x2="64"
          y2="64"
          gradientUnits="userSpaceOnUse"
        >
          <stop
            offset="0%"
            stopColor={isCompleted ? '#6EE7B7' : isFailed ? '#FCD34D' : '#8B5CF6'}
            stopOpacity="0.5"
          />
          <stop
            offset="100%"
            stopColor={isCompleted ? '#10B981' : isFailed ? '#B45309' : '#4338CA'}
            stopOpacity="0.85"
          />
        </linearGradient>

        <radialGradient
          id={`${id}-eye`}
          cx="32"
          cy="32"
          r="6"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="55%" stopColor="#C4B5FD" />
          <stop offset="100%" stopColor="#6366F1" />
        </radialGradient>

        <filter
          id={`${id}-glow-eye`}
          x="-80%"
          y="-80%"
          width="260%"
          height="260%"
        >
          <feGaussianBlur stdDeviation="2.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter
          id={`${id}-glow-diamond`}
          x="-40%"
          y="-40%"
          width="180%"
          height="180%"
        >
          <feGaussianBlur stdDeviation="0.9" result="dblur" />
          <feMerge>
            <feMergeNode in="dblur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Backplate (light variant) */}
      <rect width="64" height="64" rx="14" fill={bgFill} />

      {/* ─── Camada 2 — arcos laterais (aura/scanning) ───
          Três arcos por lado, mais longos que antes — começam em y=6
          e terminam em y=58, ocupando quase toda a altura como aura.
          O arco externo é mais grosso e mais opaco; os internos
          servem como echo. */}
      {/* Apenas 2 arcos por lado — o terceiro (mais fino) cruzava a
          silhueta da face e poluía visualmente. Outer = aura,
          mid = echo. */}
      <g className={arcLeftClass} opacity={isProcessing ? 1 : 0.65}>
        <path
          d="M 12 6 C -4 18 -4 46 12 58"
          stroke={`url(#${id}-arc)`}
          strokeWidth="2.8"
          strokeLinecap="round"
          fill="none"
          opacity="0.92"
        />
        <path
          d="M 16 12 C 3 22 3 42 16 52"
          stroke={`url(#${id}-arc)`}
          strokeWidth="1.6"
          strokeLinecap="round"
          fill="none"
          opacity="0.6"
        />
      </g>
      <g className={arcRightClass} opacity={isProcessing ? 1 : 0.65}>
        <path
          d="M 52 6 C 68 18 68 46 52 58"
          stroke={`url(#${id}-arc)`}
          strokeWidth="2.8"
          strokeLinecap="round"
          fill="none"
          opacity="0.92"
        />
        <path
          d="M 48 12 C 61 22 61 42 48 52"
          stroke={`url(#${id}-arc)`}
          strokeWidth="1.6"
          strokeLinecap="round"
          fill="none"
          opacity="0.6"
        />
      </g>

      {/* Camada 3 removida — as silhuetas das faces Janus poluíam
          visualmente e não atingiam a fidelidade da referência. */}

      {/* ─── Camada 4 — diamante em 3 camadas (borda reforçada) ───
          As camadas têm gap visível entre elas e bordas mais grossas
          que a versão anterior. Em estados terminais o diamante fica
          sólido (sem animação) para sinalizar "processo encerrado". */}
      <g filter={`url(#${id}-glow-diamond)`}>
        <path
          className={outerClass}
          d="M 32 12 L 50 32 L 32 52 L 14 32 Z"
          fill={`url(#${id}-diamond-outer)`}
          stroke={isCompleted ? '#059669' : isFailed ? '#D97706' : '#4F46E5'}
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path
          className={midClass}
          d="M 32 19 L 44 32 L 32 45 L 20 32 Z"
          fill={`url(#${id}-diamond-mid)`}
          stroke={isCompleted ? '#10B981' : isFailed ? '#B45309' : '#6366F1'}
          strokeWidth="1.1"
          strokeLinejoin="round"
        />
        <path
          className={innerClass}
          d="M 32 25 L 39 32 L 32 39 L 25 32 Z"
          fill={innerFill}
          stroke={innerStroke}
          strokeWidth="0.9"
          strokeLinejoin="round"
        />
      </g>

      {/* ─── Camada 5 — olho central / check / X ──── */}
      {isProcessing && (
        <>
          <g filter={`url(#${id}-glow-eye)`}>
            <circle
              className={eyeClass}
              cx="32"
              cy="32"
              r="4.4"
              fill={`url(#${id}-eye)`}
            />
          </g>
          {/* Pupila + highlight */}
          <circle cx="32" cy="32" r="2.0" fill="#4F46E5" />
          <circle cx="31.1" cy="31.1" r="0.6" fill="#FFFFFF" opacity="0.9" />
        </>
      )}
      {isCompleted && (
        <g filter={`url(#${id}-glow-eye)`}>
          <circle cx="32" cy="32" r="4.6" fill="#10B981" />
          <path
            d="M 29.4 32.2 L 31.5 34.2 L 34.7 30.4"
            stroke="#FFFFFF"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </g>
      )}
      {isFailed && (
        <g filter={`url(#${id}-glow-eye)`}>
          <circle cx="32" cy="32" r="4.6" fill="#F59E0B" />
          <path
            d="M 30 30 L 34 34 M 34 30 L 30 34"
            stroke="#FFFFFF"
            strokeWidth="1.6"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      )}
    </svg>
  );
};

export default JanusEnrichingMark;
